/**
 * Authentication Context
 * 
 * Provides authentication state, roles, permissions, and methods throughout the app
 */

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import api from '../services/api';

// Create context
const AuthContext = createContext(null);

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Keep auth state in sync when API interceptor clears invalid credentials
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      setRoles([]);
      setPermissions([]);
      setIsAuthenticated(false);
      localStorage.removeItem('userRoles');
      localStorage.removeItem('userPermissions');
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  /**
   * Fetch user's roles from API
   */
  const fetchUserRoles = useCallback(async (userId) => {
    try {
      console.log('[AuthContext] Fetching roles for user:', userId);
      const response = await api.get(`/users/${userId}/roles`);
      console.log('[AuthContext] Roles fetched:', response.data.roles?.length || 0);
      return response.data.roles || [];
    } catch (error) {
      console.error('[AuthContext] Error fetching user roles:', error.response?.status, error.message);
      // If endpoint doesn't exist (404), just return empty array
      if (error.response?.status === 404) {
        console.log('[AuthContext] Roles endpoint not found, using empty array');
      }
      return [];
    }
  }, []);

  /**
   * Fetch user's permissions from API
   */
  const fetchUserPermissions = useCallback(async (userId) => {
    try {
      console.log('[AuthContext] Fetching permissions for user:', userId);
      const response = await api.get(`/users/${userId}/permissions`);
      console.log('[AuthContext] Permissions fetched:', response.data.permissions?.length || 0);
      return response.data.permissions || [];
    } catch (error) {
      console.error('[AuthContext] Error fetching user permissions:', error.response?.status, error.message);
      // If endpoint doesn't exist (404), just return empty array
      if (error.response?.status === 404) {
        console.log('[AuthContext] Permissions endpoint not found, using empty array');
      }
      return [];
    }
  }, []);

  /**
   * Load user roles and permissions
   */
  const loadUserRolesAndPermissions = useCallback(async (userId) => {
    const [userRoles, userPermissions] = await Promise.all([
      fetchUserRoles(userId),
      fetchUserPermissions(userId)
    ]);

    setRoles(userRoles);
    setPermissions(userPermissions);

    // Store in localStorage for persistence
    localStorage.setItem('userRoles', JSON.stringify(userRoles));
    localStorage.setItem('userPermissions', JSON.stringify(userPermissions));
  }, [fetchUserRoles, fetchUserPermissions]);

  /**
   * Check if user has a specific permission
   * @param {string} permissionName - Name of the permission to check
   * @param {Object} scope - Optional scope to validate (e.g., { competitionId: '123' })
   * @returns {boolean} Whether user has the permission
   */
  const hasPermission = useCallback((permissionName, scope = null) => {
    if (!isAuthenticated || permissions.length === 0) {
      return false;
    }

    // Check if permission exists in user's permissions
    const permission = permissions.find(p => p.name === permissionName);
    
    if (!permission) {
      return false;
    }

    // If no scope required, user has the permission
    if (!scope) {
      return true;
    }

    // If scope is required, check if it matches permission's scope
    if (permission.scope) {
      // Check if all scope keys match
      for (const key in scope) {
        if (permission.scope[key] !== scope[key]) {
          return false;
        }
      }
    }

    return true;
  }, [isAuthenticated, permissions]);

  /**
   * Check if user has any of the specified permissions
   * @param {Array<string>} permissionNames - Array of permission names
   * @returns {boolean} Whether user has any of the permissions
   */
  const hasAnyPermission = useCallback((permissionNames) => {
    return permissionNames.some(permissionName => hasPermission(permissionName));
  }, [hasPermission]);

  /**
   * Check if user has all of the specified permissions
   * @param {Array<string>} permissionNames - Array of permission names
   * @returns {boolean} Whether user has all of the permissions
   */
  const hasAllPermissions = useCallback((permissionNames) => {
    return permissionNames.every(permissionName => hasPermission(permissionName));
  }, [hasPermission]);

  /**
   * Check if user has a specific role
   * @param {string} roleName - Name of the role to check
   * @param {Object} scope - Optional scope to validate
   * @returns {boolean} Whether user has the role
   */
  const hasRole = useCallback((roleName, scope = null) => {
    if (!isAuthenticated || roles.length === 0) {
      return false;
    }

    const role = roles.find(r => r.roleName === roleName);
    
    if (!role) {
      return false;
    }

    // If no scope required, user has the role
    if (!scope) {
      return true;
    }

    // If scope is required, check if it matches role's scope
    if (role.scope) {
      for (const key in scope) {
        if (role.scope[key] !== scope[key]) {
          return false;
        }
      }
    }

    return true;
  }, [isAuthenticated, roles]);

  /**
   * Check if user is admin
   */
  const isAdmin = useCallback(() => {
    return hasRole('admin');
  }, [hasRole]);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = authService.getStoredToken();
        const storedUser = authService.getStoredUser();
        const hasToken = !!token;

        if (hasToken) {
          // Validate token with backend before marking authenticated.
          // This allows session restoration even if cached user payload is missing.
          const meResponse = await authService.getCurrentUser();
          const currentUser = meResponse?.user || storedUser;

          if (!currentUser?.id) {
            throw new Error('Unable to restore authenticated user from token');
          }

          setUser(currentUser);
          setIsAuthenticated(true);

          // Try to load cached roles and permissions
          try {
            const cachedRoles = localStorage.getItem('userRoles');
            const cachedPermissions = localStorage.getItem('userPermissions');

            if (cachedRoles) {
              setRoles(JSON.parse(cachedRoles));
            }

            if (cachedPermissions) {
              setPermissions(JSON.parse(cachedPermissions));
            }

            // Fetch fresh data in background
            await loadUserRolesAndPermissions(currentUser.id);
          } catch (error) {
            // Don't block auth state if permission endpoints fail
            console.error('[AuthContext] Error loading roles/permissions:', error);
          }
        } else {
          // Clear stale auth artifacts and keep user on login page
          if (storedUser) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            localStorage.removeItem('userRoles');
            localStorage.removeItem('userPermissions');
          }

          setUser(null);
          setIsAuthenticated(false);
          setRoles([]);
          setPermissions([]);
        }
      } catch (error) {
        // Token invalid or backend rejected current session
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userRoles');
        localStorage.removeItem('userPermissions');
        setUser(null);
        setIsAuthenticated(false);
        setRoles([]);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [loadUserRolesAndPermissions]);

  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   */
  const login = async (email, password) => {
    try {
      const data = await authService.login(email, password);
      setUser(data.user);
      setIsAuthenticated(true);
      
      // Load roles and permissions
      await loadUserRolesAndPermissions(data.user.id);
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Register new user
   * @param {Object} userData - User registration data
   */
  const register = async (userData) => {
    try {
      const data = await authService.register(userData);
      setUser(data.user);
      setIsAuthenticated(true);
      
      // Load roles and permissions
      await loadUserRolesAndPermissions(data.user.id);
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setRoles([]);
      setPermissions([]);
      setIsAuthenticated(false);
      
      // Clear cached data
      localStorage.removeItem('userRoles');
      localStorage.removeItem('userPermissions');
    } catch (error) {
      // Clear state even if API call fails
      setUser(null);
      setRoles([]);
      setPermissions([]);
      setIsAuthenticated(false);
      localStorage.removeItem('userRoles');
      localStorage.removeItem('userPermissions');
      throw error;
    }
  };

  /**
   * Update user data
   * @param {Object} userData - Updated user data
   */
  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  /**
   * Login with JWT token (OAuth flow)
   * @param {string} token - JWT token from OAuth provider
   */
  const loginWithToken = async (token) => {
    try {
      console.log('[AuthContext] loginWithToken called');
      const data = await authService.loginWithToken(token);
      console.log('[AuthContext] Token validated, user data received');
      
      setUser(data.user);
      setIsAuthenticated(true);
      
      // Load roles and permissions
      console.log('[AuthContext] Loading user roles and permissions...');
      await loadUserRolesAndPermissions(data.user.id);
      console.log('[AuthContext] Login with token complete');
      
      return data;
    } catch (error) {
      console.error('[AuthContext] Error in loginWithToken:', error);
      throw error;
    }
  };

  /**
   * Refresh user roles and permissions
   */
  const refreshPermissions = async () => {
    if (user?.id) {
      await loadUserRolesAndPermissions(user.id);
    }
  };

  const value = {
    user,
    roles,
    permissions,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    updateUser,
    loginWithToken,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isAdmin,
    refreshPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
